import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import { WEB_API_BASE_PATH } from "common/constants";
import { filterList } from "common/utils/search";
import { useFetch } from "lib/fetcher";
import { useEffect, useMemo, useState } from "react";
import Loading from "ui/components/Loading";
import Modal from "ui/components/Modal";
import Thumbnail from "ui/components/Thumbnail";
import SearchInput from "ui/search/SearchInput";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import { Message } from "ui/text/Message";
import styles from "./SelectBuilderModal.module.scss";

interface SelectBuilderModalProps {
  onClose: () => void;
  onSelect: ((id: string, name: string | null) => void) | undefined;
  /** The list of builders to display in the modal. If not provided, the list will be fetched from the API. */
  builderList?: ClientBuilder[];
}

export default function SelectBuilderModal({ onClose, onSelect, builderList = [] }: SelectBuilderModalProps) {
  const {
    data = [],
    mutate,
    loading,
  } = useFetch<ClientBuilder[]>(`${WEB_API_BASE_PATH}/users/me/builders`, [!builderList.length]);

  const [searchQuery, setSearchQuery] = useState("");

  const builders = useMemo(() => {
    return filterList(builderList.length > 0 ? builderList : data, searchQuery);
  }, [builderList, data, searchQuery]);

  useEffect(() => {
    void mutate();
  }, []);

  /* The escape key exits the modal */
  useEffect(() => {
    const keyUp = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keyup", keyUp);

    return () => {
      document.removeEventListener("keyup", keyUp);
    };
  }, []);

  const columns: Column<ClientBuilder>[] = useMemo(
    () => [
      { header: "Name", field: "companyName", bold: true },
      {
        header: "Company Logo",
        id: "logo",
        cell: ({ row }) => (row.original.logoUrl ? <Thumbnail image={row.original.logoUrl} size="small" /> : ""),
      },
      { header: "Last Updated", field: "updatedAt", type: "DATE" },
    ],
    [],
  );

  return (
    <Modal title="Select a Builder" open scrollModal onClose={onClose}>
      <div className={styles.header}>
        <SearchInput
          type="text"
          inputSize="medium"
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          placeholder="Search"
          value={searchQuery}
          clearSearch={() => setSearchQuery("")}
        />
      </div>

      <div className={styles.builderList}>
        {builders.length > 0 ? (
          <ConfigTable
            columns={columns}
            data={builders}
            onRowClick={(row) => {
              onSelect?.(row.original.id, row.original.companyName);
              onClose();
            }}
          />
        ) : (
          <>
            {!builderList.length && loading ? (
              <Loading text="Loading Builders..." />
            ) : (
              <Message centered>No builders found.</Message>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
